import { EntityPanelFormPersonBaseKey, EntityPanelPersonsForm } from '../types';
import { validationError } from '../validation';
import { FieldError, FieldErrors } from 'react-hook-form';
import { isPhoneHaveLetters } from '../../../../event-panel/event-panel-form/helpers/person-helpers';
import { CadFeatureMap, EventPanelOptions } from '@mark43/cad-api';
import { get, isInteger, set, some, toNumber } from 'lodash';
import { isAfter } from 'date-fns';

interface ValidatePersonsPropsOptions {
    form: EntityPanelPersonsForm;
    isCad?: boolean;
    baseKey: EntityPanelFormPersonBaseKey;
    enabledFeatures: CadFeatureMap;
    eventPanelOptions?: EventPanelOptions;
    userIsAttemptingTransmit?: boolean;
    existingErrors: FieldErrors<EntityPanelPersonsForm>;
    entityExists: boolean;
}

function hasValid4DigitYear(timestamp: string) {
    const yearStr = timestamp.slice(0, 4);
    return /^[1-9]\d{3}$/.test(yearStr);
}

export const validateEntityPanelPersonsForm = ({
    form,
    isCad,
    baseKey,
    enabledFeatures,
    eventPanelOptions,
    userIsAttemptingTransmit,
    entityExists,
    existingErrors,
}: ValidatePersonsPropsOptions) => {
    for (let index = 0; index < form[baseKey].length; index++) {
        const person = form[baseKey][index];
        const localErrors: Partial<
            Record<keyof EntityPanelPersonsForm[typeof baseKey][number], FieldError>
        > = {};
        if (!person) {
            return;
        }

        if (
            isCad &&
            !userIsAttemptingTransmit &&
            enabledFeatures.reportingPartyNameRequired &&
            !person.lastName &&
            baseKey !== 'involvedPersons' &&
            !entityExists
        ) {
            localErrors.lastName = validationError('Required');
        }

        const phoneNumber = person.phoneNumber ?? '';
        let failedLettersTest = false;
        if (isPhoneHaveLetters(phoneNumber)) {
            localErrors.phoneNumber = validationError('Cannot contain letters');
            failedLettersTest = true;
        }
        const strippedPhoneNumber = phoneNumber.replace(/\W/g, '');
        if (enabledFeatures.phoneAreaCodeOptional && !failedLettersTest) {
            if (
                strippedPhoneNumber &&
                strippedPhoneNumber.length !== 7 &&
                strippedPhoneNumber.length !== 10
            ) {
                localErrors.phoneNumber = validationError('Must be 7 or 10 digits');
            }
        } else if (!failedLettersTest) {
            if (strippedPhoneNumber && strippedPhoneNumber.length !== 10) {
                localErrors.phoneNumber = validationError('Must be 10 digits');
            }
        }

        const phoneExtension = person.phoneExtension ?? '';
        if (phoneExtension.length > 10) {
            localErrors.phoneExtension = validationError('Must be 10 digits or less');
        } else if (isPhoneHaveLetters(phoneExtension)) {
            localErrors.phoneExtension = validationError('Cannot contain letters');
        }

        const validateInteger = (keyName: keyof EntityPanelPersonsForm[typeof baseKey][number]) => {
            const value = person[keyName];
            if (value !== null && value !== undefined && !isInteger(toNumber(value))) {
                localErrors[keyName] = validationError('Must be a whole number');
            }
        };

        const toTotalInches = (height: string | null): number => {
            return toNumber(height?.[0] || 0) * 12 + toNumber(height?.slice(1) || 0);
        };

        // Height
        // Validate they're all numbers
        ([
            'height',
            'heightMax',
            'heightMin',
        ] as (keyof EntityPanelPersonsForm[typeof baseKey][number])[]).forEach(validateInteger);

        // Height Range
        const noHeightRangeErrors = !(
            localErrors.height ||
            localErrors.heightMin ||
            localErrors.heightMax
        );

        const needToCompareRanges = person.heightMin && person.heightMax;

        // Don't override past errors, compare ranges if we have at least one field on both sides of the range
        if (noHeightRangeErrors && needToCompareRanges) {
            // Validate computed heightMin is lower than computed heightMax
            const computedMin = toTotalInches(person.heightMin);
            const computedMax = toTotalInches(person.heightMax);

            if (computedMin > computedMax) {
                localErrors.heightMin = validationError('Must be less than max');
                localErrors.heightMax = validationError('Must be greater than min');
            }
        }

        // Weight
        // Validate they're all numbers
        ([
            'weight',
            'weightMin',
            'weightMax',
        ] as (keyof EntityPanelPersonsForm[typeof baseKey][number])[]).forEach(validateInteger);

        if (
            person.weightMin &&
            person.weightMax &&
            toNumber(person.weightMin) >= toNumber(person.weightMax)
        ) {
            // Validate computed weightMin is lower than computed weightMax
            localErrors.weightMin = validationError('Must be less than max');
            localErrors.weightMax = validationError('Must be greater than min');
        }

        // DOB Range
        (['ageMin', 'ageMax'] as (keyof EntityPanelPersonsForm[typeof baseKey][number])[]).forEach(
            validateInteger
        );

        // Validate DOB start is before DOB after
        const { ageMin, ageMax, dateOfBirth } = person;

        if (ageMin && ageMax && toNumber(ageMin) > toNumber(ageMax)) {
            localErrors.ageMin = validationError('Must be less than max');
            localErrors.ageMax = validationError('Must be greater than min');
        }

        if (dateOfBirth && !hasValid4DigitYear(dateOfBirth)) {
            localErrors.dateOfBirth = validationError('Year of birth must be in 4 digits format');
        }
        if (dateOfBirth && isAfter(new Date(dateOfBirth), new Date())) {
            localErrors.dateOfBirth = validationError('Date of birth is after today');
        }

        // Query in DEx
        if (person.queryInDex) {
            if (
                !some([
                    person.lastName,
                    person.firstName,
                    person.dateOfBirth,
                    person.driversLicenseNumber,
                    person.driversLicenseState,
                    person.race,
                    person.sex,
                ])
            ) {
                localErrors.queryInDex = validationError(
                    'Name, DOB, Age, DL Info, Race, or Sex required'
                );
            }
        }

        if (
            eventPanelOptions?.purposeCodeOptions.length &&
            !person.isPersisted &&
            person.queryInDex &&
            !person.purposeCode
        ) {
            localErrors.purposeCode = validationError('Required');
        }

        if (Object.keys(localErrors).length > 0) {
            if (!existingErrors[baseKey]) {
                existingErrors[baseKey] = [];
            }
            if (!get(existingErrors, `${baseKey}[${index}]`)) {
                set(existingErrors, `${baseKey}[${index}]`, localErrors);
            }
        }
    }
};
